import React from "react";
import { useDispatch } from "react-redux";
import FeedsFilterBar from "web/react/components/feeds-filters/feeds-filter-bar/feeds-filter-bar";
import SuggestionPills from "web/react/components/feeds-filters/feeds-suggested-filters-pills/feeds-suggested-filters-pills";
import {
    getSelectedOptions,
    useFilterManager,
} from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { openFeedsFiltersOverlay } from "web/react/components/feeds-filters/hooks/use-overlay-handler";
import { useActiveFilters } from "web/react/hooks/filters";
import { useFilterSync } from "web/react/hooks/filters/use-filter-sync";
import { useSuggestedFilters } from "web/react/hooks/filters/use-suggested-filters";
import { useInitialReduxState } from "web/react/hooks/use-inital-redux-state/use-initial-redux-state";
import { openSidebar } from "web/redux/ducks/feed-sidebar";
import { setFiltersData } from "web/redux/ducks/filters";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import { FeedFiltersSerializer } from "web/types/serializers";
import { UniversalFiltersData } from "web/types/universal-filters";

interface FilterContainerProps {
    universalFilters?: UniversalFiltersData;
    filters?: FeedFiltersSerializer;
}

export function FilterContainerTop({
    filters,
    universalFilters,
}: FilterContainerProps): JSX.Element {
    useInitialReduxState([[setFiltersData, filters]]);

    if (!environment.getFeature("uf_enable_redesign_mobile_filter")) {
        return (
            <FilterContainerTopDefault
                universalFilters={universalFilters as UniversalFiltersData}
            />
        );
    }

    return <FilterContainerTopRedesign />;
}

export default FilterContainerTop;

function FilterContainerTopDefault({
    universalFilters,
}: Required<Pick<FilterContainerProps, "universalFilters">>): JSX.Element {
    const {
        selectedFilterInfo,
        suggestedFilters,
        onClick: onFilterManagerClick,
    } = useFilterManager(universalFilters);

    const selectedOptions = getSelectedOptions(selectedFilterInfo);

    const breadcrumbs = selectedOptions.map((option) => ({
        id: `${option.name}=${option.value}`,
        to: option.url,
        label: option.label,
        onClick: () => {
            onFilterManagerClick({ ...option, event_prefix: "filter_mobile" }, true, true);
        },
        hideIcon: option.name === "gender",
    }));

    function onSuggestionPillClick(option): void {
        onFilterManagerClick({ ...option, event_prefix: "filter_suggested" }, true, true);
    }

    return (
        <div>
            <FeedsFilterBar
                onFilterClick={() => openFeedsFiltersOverlay()}
                breadcrumbs={breadcrumbs}
            />
            <SuggestionPills suggestedFilters={suggestedFilters} onClick={onSuggestionPillClick} />
        </div>
    );
}

function FilterContainerTopRedesign(): JSX.Element {
    const dispatch = useDispatch();
    const { onChange } = useFilterSync();
    const suggestedFilters = useSuggestedFilters();
    const { filters } = useActiveFilters();

    const suggestedOptions =
        suggestedFilters?.options?.map(({ url, label, facet }) => ({
            url,
            label,
            facet,
            name: label,
            value: url,
        })) || [];

    function onSuggestionPillClick(option): void {
        // Track the clicked option and update the Product feed
        onChange(option.url, true);
        analytics.event("feed", "add_filter", `filter_suggested_${option.facet}`);
    }

    const breadcrumbs =
        filters?.map((filter) => ({
            id: filter.id,
            to: filter.url,
            label: filter.label,
            onClick: () => onChange(filter.url, true),
            hideIcon: filter.facet === "gender",
        })) || [];

    return (
        <div>
            <FeedsFilterBar
                onFilterClick={() => dispatch(openSidebar())}
                breadcrumbs={breadcrumbs}
            />
            <SuggestionPills suggestedFilters={suggestedOptions} onClick={onSuggestionPillClick} />
        </div>
    );
}
